/**
 * Matter Progress Indicator
 * Shows progress as a row of filled/empty circles with optional tooltip
 */

'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { ClipboardCheck, Scale, Search, Share2, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import type { Stage } from './types';

// Stage icons mirror the in-matter "Matter Progress" panel
// (matter-progress-card.tsx STAGE_ICONS), keyed by the display name the list
// tooltip receives via `mapStageSlugToName`, so the two surfaces stay visually
// consistent. Names without a mapped icon (e.g. "Finalization") render a
// same-width spacer to keep the rows aligned.
const STAGE_ICONS: Record<string, LucideIcon> = {
  'Initial Setup': ClipboardCheck,
  Discovery: Search,
  Grant: Scale,
  Redemption: Wallet,
  Distribution: Share2
};

interface MatterProgressProps {
  total: number;
  completed: number;
  stages?: Stage[];
  className?: string;
  compact?: boolean;
}

function getStatusDisplay(status: Stage['status']): {
  text: string;
  className: string;
} {
  switch (status) {
    case 'completed':
      return {
        text: 'Completed',
        className: 'text-emerald-700' // Matches the Matter Progress panel's completed color
      };
    case 'in-progress':
      return {
        text: 'In Progress',
        className: 'text-blue-700' // Matches the Matter Progress panel's in-progress color
      };
    case 'not-started':
      return { text: 'Not Started', className: 'text-muted-foreground' };
  }
}

export function MatterProgress({
  total,
  completed,
  stages,
  className
}: MatterProgressProps) {
  const progressDots = (
    <div className={cn('flex items-center gap-1 py-1', className)}>
      {Array.from({ length: total }).map((_, index) => {
        // If stages are provided, use actual stage status for color
        // Otherwise, fall back to simple count-based coloring
        let dotColor: string;

        if (stages && stages[index]) {
          // Use stage status for color
          switch (stages[index].status) {
            case 'completed':
              dotColor = 'bg-emerald-500'; // Matches the Matter Progress panel's completed color
              break;
            case 'in-progress':
              dotColor = 'bg-progress'; // Blue for active work, matches Matter Progress
              break;
            case 'not-started':
            default:
              dotColor = 'bg-muted-foreground/20'; // Grey
              break;
          }
        } else {
          // Fallback to count-based coloring
          dotColor =
            index < completed ? 'bg-progress' : 'bg-muted-foreground/20';
        }

        return (
          <div
            // eslint-disable-next-line @eslint-react/no-array-index-key -- progress dots are positional indicators; index IS the identity (stage position)
            key={index}
            className={cn('h-2 w-2 rounded-full transition-colors', dotColor)}
          />
        );
      })}
    </div>
  );

  // If no stages provided, just show the dots without tooltip
  if (!stages || stages.length === 0) {
    return progressDots;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{progressDots}</TooltipTrigger>
        {/*
          Portal escapes the parent card's stacking context. The card's
          `animate-card-fade-in-down` animates transform/opacity, which promotes
          it to its own stacking context — without portaling, the tooltip is
          trapped beneath sibling cards regardless of z-index.
        */}
        <TooltipPrimitive.Portal>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="space-y-1">
              <p className="text-xs font-semibold">Matter Progress</p>
              <ul className="space-y-0.5 text-xs">
                {stages.map((stage) => {
                  const { text, className: statusClassName } = getStatusDisplay(
                    stage.status
                  );
                  const Icon = STAGE_ICONS[stage.name];
                  return (
                    <li key={stage.name} className="flex items-center gap-1.5">
                      {Icon ? (
                        <Icon
                          className={cn(
                            'h-3.5 w-3.5 shrink-0',
                            statusClassName
                          )}
                          aria-hidden="true"
                        />
                      ) : (
                        <span className="w-3.5 shrink-0" aria-hidden="true" />
                      )}
                      <span>
                        {stage.name}:{' '}
                        <span className={statusClassName}>{text}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );
}
